import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, ChevronUp, Download, Drum, Guitar, Headphones, Layers, Loader2, Mic2, Pause, Play, RotateCcw, SlidersHorizontal, Volume2, VolumeX } from "lucide-react";
import { Button } from "../ui/button";
import { resolveMediaUrl } from "../../lib/mediaUrl";
import { nativeAudioDownload, requestAudioExport } from "../../lib/stemAudioExport";
import { mediaUrl } from "../../lib/publicAudioTools";
import { STEM_NAMES, stemStatusLabel, stemsAreReady } from "./stemTask";
import { createStemTransport, formatDb, formatStemTime, newStemMix, stemGain } from "./stemTransport";
import "./stems.css";

const META = {
  vocals: { label: "Vocals", Icon: Mic2, color: "text-signal" },
  drums: { label: "Drums", Icon: Drum, color: "text-brass" },
  bass: { label: "Bass", Icon: Guitar, color: "text-tune" },
  other: { label: "Other", Icon: Layers, color: "text-muted-foreground" },
};

export function StemWaveform({ peaks, progress, expanded, label }) {
  const bars = useMemo(() => {
    if (!Array.isArray(peaks) || !peaks.length || peaks.some(value => !Number.isFinite(value) || value < 0 || value > 1)) return [];
    const count = Math.min(160, peaks.length);
    return Array.from({ length: count }, (_, index) => {
      const start = Math.floor(index * peaks.length / count);
      const end = Math.floor((index + 1) * peaks.length / count);
      return Math.max(...peaks.slice(start, end));
    });
  }, [peaks]);
  return <div className={`stem-waveform ${expanded ? "is-expanded" : ""}`}>
    {bars.length ? <svg viewBox="0 0 320 80" preserveAspectRatio="none" role="img" aria-label={`Forma de onda ${label}`}>
      {bars.map((peak, index) => <line key={index} x1={(index + 0.5) * 320 / bars.length} x2={(index + 0.5) * 320 / bars.length}
        y1={40 - peak * 38} y2={40 + peak * 38} stroke="currentColor" strokeWidth={Math.min(3, 240 / bars.length)}
        opacity={index / bars.length <= progress ? 0.95 : 0.3} />)}
      <line x1={progress * 320} x2={progress * 320} y1="0" y2="80" stroke="currentColor" strokeWidth="1" />
    </svg> : <span className="text-xs text-muted-foreground">Forma de onda no disponible</span>}
  </div>;
}

function IconButton({ label, children, ...props }) {
  return <Button type="button" variant="outline" size="icon" title={label} aria-label={label} {...props}>{children}</Button>;
}

const StemQuickMixer = forwardRef(function StemQuickMixer({ task, analysisId, duration = 0, getAuthHeaders, onRetry, retrying, statusError, onBeforePlay }, ref) {
  const [mix, setMix] = useState(newStemMix);
  const [masterDb, setMasterDb] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [playback, setPlayback] = useState({ time: 0, playing: false, buffering: false, temporarySolo: null, error: "" });
  const [download, setDownload] = useState({ busy: false, progress: 0, error: "", result: null });
  const transport = useRef(null);
  const downloadController = useRef(null);
  const beforePlay = useRef(onBeforePlay);
  beforePlay.current = onBeforePlay;
  const ready = stemsAreReady(task);
  const taskDuration = Math.max(0, duration || task?.stem_metadata?.vocals?.duration || 0);
  const failed = task?.status === "failed" || ["failed", "stale"].includes(task?.processing_status);
  const taskId = task?.id;
  const urls = useMemo(() => ready ? Object.fromEntries(STEM_NAMES.map(name => [name, resolveMediaUrl(task.stems[name])])) : null,
    [ready, task?.stems]);

  useEffect(() => {
    setPlayback({ time: 0, playing: false, buffering: false, temporarySolo: null, error: "" });
    if (!urls) return undefined;
    const player = createStemTransport({ urls, duration: taskDuration, onChange: setPlayback, onBeforePlay: () => beforePlay.current?.() });
    transport.current = player;
    return () => { player.dispose(); if (transport.current === player) transport.current = null; };
  }, [urls, taskId, taskDuration]);
  useEffect(() => { transport.current?.setMix(mix, masterDb); }, [mix, masterDb, urls, taskId, taskDuration]);
  useImperativeHandle(ref, () => ({ pause: () => transport.current?.pause() }), []);
  useEffect(() => {
    setDownload({ busy: false, progress: 0, error: "", result: null });
    return () => { downloadController.current?.abort(); downloadController.current = null; };
  }, [taskId]);

  const downloadStem = async name => {
    if (!ready || downloadController.current) return;
    const controller = new AbortController(); downloadController.current = controller;
    setDownload({ busy: true, progress: 0, error: "", result: null });
    try {
      const result = await requestAudioExport(`/stems/task/${taskId}/exports`, { format: "wav", stem: name }, getAuthHeaders(), controller.signal,
        progress => { if (!controller.signal.aborted) setDownload(prev => ({ ...prev, progress })); });
      if (controller.signal.aborted) return;
      const asset = result.assets?.find(item => name ? item.asset === `${name}.wav` : item.asset === "stems.zip");
      if (!asset) throw new Error("La descarga no esta disponible");
      nativeAudioDownload(asset);
      setDownload({ busy: false, progress: 100, error: "", result });
    } catch (error) {
      if (!controller.signal.aborted) setDownload({ busy: false, progress: 0, error: error.response?.data?.detail?.message || error.message || "No se pudo descargar", result: null });
    } finally { if (downloadController.current === controller) downloadController.current = null; }
  };
  const toggleMix = (name, key) => setMix(prev => ({ ...prev, [name]: { ...prev[name], [key]: !prev[name][key] } }));
  const setDb = (name, db) => setMix(prev => ({ ...prev, [name]: { ...prev[name], db } }));
  const active = playback.playing || playback.buffering;
  const progress = taskDuration ? Math.min(1, playback.time / taskDuration) : 0;

  return <section className="stem-quick-mixer" aria-label="Stem Player / Quick Mixer">
    <header className="stem-section-header">
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-foreground">Separacion de pistas</h2>
        <p role="status" className={`stem-status ${ready ? "text-tune" : "text-muted-foreground"}`}>
          {ready ? <Check size={16} /> : !failed && task?.processing_status !== "not_required" ? <Loader2 size={16} className="motion-safe:animate-spin" /> : null}
          <span>{stemStatusLabel(task)}</span>
        </p>
      </div>
      <div className="stem-header-actions">
        {ready && <Button asChild variant="outline" className="min-h-11"><Link to={`/stem-mixer/${analysisId}?task=${encodeURIComponent(taskId)}`}>
          <SlidersHorizontal size={16} />Abrir mezclador
        </Link></Button>}
        <Button variant="outline" className="min-h-11" disabled={!ready || download.busy} onClick={() => downloadStem(null)}>
          {download.busy ? <Loader2 size={16} className="motion-safe:animate-spin" /> : <Download size={16} />}Descargar stems
        </Button>
        {failed && <Button variant="outline" className="min-h-11" disabled={retrying} onClick={onRetry}>
          <RotateCcw size={16} />{retrying ? "Reintentando..." : "Reintentar separacion"}
        </Button>}
      </div>
    </header>
    {(statusError || failed) && <p role="alert" className="stem-message text-destructive">{statusError || task.error || stemStatusLabel(task)}</p>}
    {ready && <div className="stem-master">
      <IconButton label={active && !playback.temporarySolo ? "Pausar mezcla" : "Reproducir mezcla"} className="stem-master-play"
        onClick={() => active && !playback.temporarySolo ? transport.current?.pause() : transport.current?.play()}>
        {playback.buffering ? <Loader2 className="motion-safe:animate-spin" /> : playback.playing && !playback.temporarySolo ? <Pause /> : <Play />}
      </IconButton>
      <div className="stem-seek">
        <input type="range" min="0" max={taskDuration || 1} step="0.01" value={playback.time} aria-label="Posicion de la mezcla"
          aria-valuetext={`${formatStemTime(playback.time)} de ${formatStemTime(taskDuration)}`} disabled={!taskDuration}
          onChange={event => transport.current?.seek(Number(event.target.value))} />
        <div className="flex justify-between gap-2 text-xs tabular-nums text-muted-foreground">
          <span>{formatStemTime(playback.time)}</span><span>{formatStemTime(taskDuration)}</span>
        </div>
      </div>
      <label className="stem-master-volume"><Volume2 size={16} aria-hidden="true" /><span className="sr-only">Volumen master</span>
        <input type="range" min="-60" max="0" step="1" value={masterDb} aria-label="Volumen master" aria-valuetext={formatDb(masterDb)} onChange={event => setMasterDb(Number(event.target.value))} />
        <output>{formatDb(masterDb)}</output>
      </label>
      <IconButton label="Restablecer mezcla" onClick={() => { setMix(newStemMix()); setMasterDb(0); if (playback.temporarySolo) transport.current?.play(); }}><RotateCcw /></IconButton>
    </div>}
    {playback.buffering && <p role="status" className="stem-message text-muted-foreground">Cargando audio...</p>}
    {playback.error && <p role="alert" className="stem-message text-destructive">{playback.error}</p>}
    <div className="stem-card-grid" aria-busy={!ready && !failed && task?.processing_status !== "not_required"}>
      {STEM_NAMES.map(name => {
        const { Icon, label, color } = META[name];
        const metadata = task?.stem_metadata?.[name];
        const audible = stemGain(name, mix, masterDb, playback.temporarySolo) > 0;
        const individualActive = active && playback.temporarySolo === name;
        return <article key={name} className={`stem-card ${ready && !audible ? "is-muted" : ""} ${playback.playing && audible ? "is-playing" : ""}`}>
          <div className="stem-card-heading">
            <button type="button" className="stem-expand" aria-expanded={Boolean(expanded[name])} aria-controls={`stem-details-${name}`} onClick={() => setExpanded(prev => ({ ...prev, [name]: !prev[name] }))}>
              <Icon size={24} className={color} /><span className="min-w-0 text-left"><span className="block font-semibold">{label}</span>
                <span className="block text-xs font-normal text-muted-foreground">{ready ? (playback.playing && audible ? "Reproduciendo" : !audible ? "Silenciado" : "Listo") : failed ? "No disponible" : "Preparando"}</span></span>
              {expanded[name] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            <IconButton label={`Descargar ${label}`} disabled={!ready || download.busy} onClick={() => downloadStem(name)}><Download /></IconButton>
          </div>
          {ready ? <div className={color}><StemWaveform peaks={metadata?.peaks} progress={progress} expanded={expanded[name]} label={label} /></div> :
            <div className="stem-skeleton motion-safe:animate-pulse" aria-hidden="true"><span /><span /></div>}
          <div className="stem-card-controls">
            <IconButton label={`${individualActive ? "Pausar" : "Reproducir"} ${label}`} disabled={!ready} aria-pressed={individualActive}
              onClick={() => individualActive ? transport.current?.pause() : transport.current?.play(name)}>{individualActive ? <Pause /> : <Play />}</IconButton>
            <IconButton label={`Mute ${label}`} disabled={!ready} aria-pressed={mix[name].muted} onClick={() => toggleMix(name, "muted")}>{mix[name].muted ? <VolumeX /> : <Volume2 />}</IconButton>
            <IconButton label={`Solo ${label}`} disabled={!ready} aria-pressed={mix[name].solo} onClick={() => toggleMix(name, "solo")}><Headphones /></IconButton>
            <label className="stem-volume"><span className="sr-only">Volumen {label}</span>
              <input type="range" min="-60" max="0" step="1" disabled={!ready} value={mix[name].db} aria-label={`Volumen ${label}`} aria-valuetext={formatDb(mix[name].db)} onChange={event => setDb(name, Number(event.target.value))} />
              <output>{formatDb(mix[name].db)}</output>
            </label>
          </div>
          {expanded[name] && <dl id={`stem-details-${name}`} className="stem-metadata text-xs text-muted-foreground">
            <div><dt>Duracion</dt><dd>{metadata?.duration ? formatStemTime(metadata.duration) : "--"}</dd></div>
            <div><dt>Frecuencia</dt><dd>{metadata?.sample_rate ? `${metadata.sample_rate} Hz` : "--"}</dd></div>
            <div><dt>Canales</dt><dd>{metadata?.channels || "--"}</dd></div>
            <div><dt>Formato</dt><dd>{task?.storage_format || "--"}</dd></div>
            <div className="stem-metadata-wide"><dt>Version</dt><dd>{task?.separation_version || task?.model_version || "--"}</dd></div>
            {metadata?.sha256 && <div className="stem-metadata-wide"><dt>SHA256</dt><dd>{metadata.sha256}</dd></div>}
          </dl>}
        </article>;
      })}
    </div>
    {download.busy && <p role="status" className="stem-message">Exportando stems · {Math.round(download.progress)}%</p>}
    {download.error && <p role="alert" className="stem-message text-destructive">{download.error}</p>}
    {download.result?.warnings?.map(message => <p key={message} className="stem-message text-brass">{message}</p>)}
    {download.result && <div className="stem-download-results">{download.result.assets.map(asset => <a key={asset.asset} href={mediaUrl(asset.url)} download={asset.filename} rel="noreferrer" className="text-sm text-signal underline">{asset.filename}</a>)}</div>}
  </section>;
});

export default StemQuickMixer;
